/**
 * Fixed Invoice and Payment Services
 * This file contains improved implementations to fix persistence issues with invoices and payments.
 */

// Update these imports based on your actual project structure
import { supabase } from './supabase';
import { Invoice, Payment } from './supabase';

// Fixed invoice service implementations
export const fixedInvoiceServices = {
  // Get all invoices
  async getAll() {
    try {
      console.log('Fetching all invoices');
      
      const { data, error } = await supabase
        .from('invoices')
        .select('*')
        .order('due_date', { ascending: false });
      
      if (error) {
        console.error('Error fetching invoices from Supabase:', error);
        throw error;
      }
      
      console.log(`Successfully fetched ${data.length} invoices`);
      return { data, error };
    } catch (err: any) {
      console.error('Error in invoice fetch:', err);
      return { data: null, error: err };
    }
  },

  // Get invoice by id
  async getById(id: string) {
    try {
      if (!id) {
        throw new Error('Invoice ID is required');
      }

      console.log(`Fetching invoice with ID: ${id}`);
      
      const { data, error } = await supabase
        .from('invoices')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error) {
        console.error(`Error fetching invoice ${id} from Supabase:`, error);
        throw error;
      }
      
      console.log('Successfully fetched invoice:', data);
      return { data, error };
    } catch (err: any) {
      console.error('Error in invoice fetch by ID:', err);
      return { data: null, error: err };
    }
  },

  // Get invoices by subscription_id
  async getBySubscription(subscriptionId: string) {
    try {
      if (!subscriptionId) {
        throw new Error('Subscription ID is required');
      }

      console.log(`Fetching invoices for subscription: ${subscriptionId}`);
      
      const { data, error } = await supabase
        .from('invoices')
        .select('*')
        .eq('subscription_id', subscriptionId)
        .order('due_date', { ascending: false });
      
      if (error) {
        console.error(`Error fetching invoices for subscription ${subscriptionId}:`, error);
        throw error;
      }
      
      console.log(`Successfully fetched ${data.length} invoices for subscription`);
      return { data, error };
    } catch (err: any) {
      console.error('Error in invoice fetch by subscription:', err);
      return { data: null, error: err };
    }
  },

  // Create a new invoice with proper error handling and data formatting
  async create(invoice: Invoice) {
    try {
      // Ensure required fields are present
      if (!invoice.subscription_id || !invoice.amount || !invoice.status || !invoice.due_date) {
        throw new Error('Missing required invoice fields');
      }

      // Format the data correctly for Supabase
      const formattedInvoice = {
        ...invoice,
        // Format dates if necessary
        due_date: invoice.due_date,
        paid_at: invoice.paid_at || null,
        amount: Number(invoice.amount) // Ensure amount is a number
      };
      
      console.log('Creating invoice with data:', formattedInvoice);
      
      const { data, error } = await supabase
        .from('invoices')
        .insert([formattedInvoice])
        .select();
      
      if (error) {
        console.error('Error creating invoice in Supabase:', error);
        throw error;
      }
      
      console.log('Invoice created successfully:', data);
      return { data, error };
    } catch (err: any) {
      console.error('Error in invoice creation:', err);
      return { data: null, error: err };
    }
  },

  // Update an invoice with proper error handling and data formatting
  async update(id: string, updates: Partial<Invoice>) {
    try {
      if (!id) {
        throw new Error('Invoice ID is required for updates');
      }

      // Format the data correctly for Supabase
      const formattedUpdates = {
        ...updates,
        // Format dates if present
        due_date: updates.due_date,
        paid_at: updates.paid_at,
        // Ensure amount is a number if present
        amount: updates.amount !== undefined ? Number(updates.amount) : undefined,
        // Ensure updated_at is current
        updated_at: new Date().toISOString(),
      };

      console.log(`Updating invoice ${id} with data:`, formattedUpdates);

      const { data, error } = await supabase
        .from('invoices')
        .update(formattedUpdates)
        .eq('id', id)
        .select();
      
      if (error) {
        console.error('Error updating invoice in Supabase:', error);
        throw error;
      }
      
      console.log('Invoice updated successfully:', data);
      return { data, error };
    } catch (err: any) {
      console.error('Error in invoice update:', err);
      return { data: null, error: err };
    }
  },

  // Delete an invoice with proper error handling
  async delete() {
    return { 
      data: null, 
      error: new Error('Invoices cannot be deleted. Use cancel() to cancel an invoice instead.') 
    };
  },

  // Calculate invoice total paid amount and update status accordingly
  async updatePaymentStatus(id: string) {
    try {
      if (!id) {
        throw new Error('Invoice ID is required');
      }

      // Fetch invoice with all its payments
      const { data: invoice, error: invoiceError } = await supabase
        .from('invoices')
        .select('*, payments(*)')
        .eq('id', id)
        .single();

      if (invoiceError || !invoice) {
        throw invoiceError || new Error('Invoice not found');
      }

      // Filter and calculate total from successful payments only
      const successfulPayments = ((invoice.payments as Payment[]) || [])
        .filter((p: Payment) => p.status === 'success')
        .sort((a, b) => new Date(a.paid_at).getTime() - new Date(b.paid_at).getTime());
        
      const totalPaid = Math.round(successfulPayments
        .reduce((sum: number, payment: Payment) => sum + Number(payment.amount), 0) * 100) / 100; // Round to 2 decimal places
      
      // Get the latest successful payment date
      const latestPaymentDate = successfulPayments.length > 0 ? 
        successfulPayments[successfulPayments.length - 1].paid_at : null;

      // Calculate remaining amount with proper rounding
      const remainingAmount = Math.max(0, Math.round((invoice.amount - totalPaid) * 100) / 100);
      const currentDate = new Date();
      const dueDate = new Date(invoice.due_date);

      // Determine status based on payment amount, due date, and cancellation
      let newStatus: Invoice['status'];
      let updatedPaidAt: string | null = null;

      if (invoice.status === 'Cancelled') {
        // Cancelled invoices stay cancelled
        newStatus = 'Cancelled';
      } else if (remainingAmount === 0) {
        // Fully paid invoice
        newStatus = 'Paid';
        updatedPaidAt = latestPaymentDate || currentDate.toISOString();
      } else if (totalPaid > 0) {
        // Partially paid invoice
        newStatus = 'Partially Paid';
        if (dueDate < currentDate) {
          newStatus = 'Overdue';
        }
      } else if (dueDate < currentDate) {
        // Unpaid and past due date
        newStatus = 'Overdue';
      } else {
        // Unpaid and not yet due
        newStatus = 'Unpaid';
      }

      // Update invoice with calculated values
      const { data, error: updateError } = await supabase
        .from('invoices')
        .update({
          status: newStatus,
          total_paid: totalPaid,
          paid_at: updatedPaidAt,
          updated_at: currentDate
        })
        .eq('id', id)
        .select();

      if (updateError) {
        throw updateError;
      }

      console.log('Invoice payment status updated:', data);
      return { data, error: null };
    } catch (err: any) {
      console.error('Error updating invoice payment status:', err);
      return { data: null, error: err };
    }
  },

  // Cancel invoice using Edge Function
  async cancel(id: string) {
    try {
      if (!id) {
        throw new Error('Invoice ID is required for cancellation');
      }

      const { data, error } = await supabase.functions.invoke('cancel-invoice', {
        body: { invoice_id: id }
      });

      if (error) throw error;

      if (data.error) {
        throw new Error(data.error);
      }

      return { data: [data.data], error: null };
    } catch (err: any) {
      console.error('Error in invoice cancellation:', err);
      return { data: null, error: err };
    }
  }
};

// Fixed payment service implementations
export const fixedPaymentServices = {
  // Get all payments
  async getAll() {
    try {
      console.log('Fetching all payments');
      
      const { data, error } = await supabase
        .from('payments')
        .select('*')
        .order('paid_at', { ascending: false });
      
      if (error) {
        console.error('Error fetching payments from Supabase:', error);
        throw error;
      }
      
      console.log(`Successfully fetched ${data.length} payments`);
      return { data, error };
    } catch (err: any) {
      console.error('Error in payment fetch:', err);
      return { data: null, error: err };
    }
  },

  // Get payment by id
  async getById(id: string) {
    try {
      if (!id) {
        throw new Error('Payment ID is required');
      }

      console.log(`Fetching payment with ID: ${id}`);
      
      const { data, error } = await supabase
        .from('payments')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error) {
        console.error(`Error fetching payment ${id} from Supabase:`, error);
        throw error;
      }
      
      console.log('Successfully fetched payment:', data);
      return { data, error };
    } catch (err: any) {
      console.error('Error in payment fetch by ID:', err);
      return { data: null, error: err };
    }
  },

  // Get payments by invoice_id
  async getByInvoice(invoiceId: string) {
    try {
      if (!invoiceId) {
        throw new Error('Invoice ID is required');
      }

      console.log(`Fetching payments for invoice: ${invoiceId}`);
      
      const { data, error } = await supabase
        .from('payments')
        .select('*')
        .eq('invoice_id', invoiceId)
        .order('paid_at', { ascending: false });
      
      if (error) {
        console.error(`Error fetching payments for invoice ${invoiceId}:`, error);
        throw error;
      }
      
      console.log(`Successfully fetched ${data.length} payments for invoice`);
      return { data, error };
    } catch (err: any) {
      console.error('Error in payment fetch by invoice:', err);
      return { data: null, error: err };
    }
  },

  // Create a new payment using Edge Function
  async create(payment: Payment) {
    try {
      // Call the Edge Function
      const { data, error } = await supabase.functions.invoke('process-payment', {
        body: { payment }
      });

      if (error) throw error;

      if (data.error) {
        throw new Error(data.error);
      }

      return { data: [data.data], error: null };
    } catch (err: any) {
      console.error('Error in payment creation:', err);
      return { data: null, error: err };
    }
  },

  // Update a payment with proper error handling and data formatting
  async update(id: string, updates: Partial<Payment>) {
    try {
      if (!id) {
        throw new Error('Payment ID is required for updates');
      }

      // Format the data correctly for Supabase
      const formattedUpdates = {
        ...updates,
        // Format dates if present
        paid_at: updates.paid_at,
        // Ensure amount is a number if present
        amount: updates.amount !== undefined ? Number(updates.amount) : undefined,
        // Ensure updated_at is current
        updated_at: new Date().toISOString(),
      };

      console.log(`Updating payment ${id} with data:`, formattedUpdates);

      const { data, error } = await supabase
        .from('payments')
        .update(formattedUpdates)
        .eq('id', id)
        .select();
      
      if (error) {
        console.error('Error updating payment in Supabase:', error);
        throw error;
      }
      
      console.log('Payment updated successfully:', data);
      return { data, error };
    } catch (err: any) {
      console.error('Error in payment update:', err);
      return { data: null, error: err };
    }
  },

  // Delete a payment with proper error handling
  async delete(id: string) {
    try {
      if (!id) {
        throw new Error('Payment ID is required for deletion');
      }
      
      console.log(`Deleting payment ${id}`);
      
      const { data, error } = await supabase
        .from('payments')
        .delete()
        .eq('id', id);
      
      if (error) {
        console.error('Error deleting payment from Supabase:', error);
        throw error;
      }
      
      console.log('Payment deleted successfully');
      return { data, error };
    } catch (err: any) {
      console.error('Error in payment deletion:', err);
      return { data: null, error: err };
    }
  }
};
